import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";

import { CaseAdmin, Patient } from "pwapi";

import {
  createQAReview,
  requestAdminCase,
  resetCaseErrorMessage,
  resetDeletionStatus,
  saveAdminCase,
  updateIntegrations,
} from "shared/actions/admin/adminCaseActions";
import { savePatient } from "shared/actions/admin/adminPatientActions";
import { SlideAdminWithUrls } from "shared/reducers/admin/adminSlideReducer";
import { FeatureFlags } from "shared/reducers/sysReducer";

import { HistoryStateType } from "components/pages/admin/AdminCasePage";
import Spinner from "components/Spinner";
import ApplicationState from "store/ApplicationState";
import { useAppSelector } from "store/hooks";

import EditCasePanel from "./EditCasePanel";

const CaseEditController = ({
  historyState,
}: {
  historyState?: HistoryStateType;
}) => {
  const case_id = parseInt(useParams<{ case_id: string }>().case_id || "0");
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const slide_params = useParams<{ slide_id: string }>().slide_id;
  const slide_id = slide_params && (parseInt(slide_params) || slide_params);
  const specimen_params = useParams<{ specimen_id: string }>().specimen_id;
  const specimen_id =
    specimen_params && (parseInt(specimen_params) || specimen_params);
  const block_params = useParams<{ block_id: string }>().block_id;
  const block_id = block_params && (parseInt(block_params) || block_params);
  const documentQuery = useParams<{ document_id: string }>().document_id;
  const document_id = parseInt(documentQuery || "0");
  const order_params = useParams<{ order_id: string }>().order_id;
  const order_id = order_params && (parseInt(order_params) || order_params);

  const dispatch = useDispatch();

  const rawAdminCase = useAppSelector(({ admin }) => admin.case);
  const adminCase = rawAdminCase
    ? {
        ...rawAdminCase,
        slides: rawAdminCase.slides.map((s: SlideAdminWithUrls) => ({
          ...s,
          case: rawAdminCase,
          specimen: rawAdminCase.specimens.find(
            (sp) => sp.id === s.specimen_id
          ),
          block_obj: rawAdminCase.blocks?.find((b) => b.id === s.block_obj_id),
        })),
      }
    : null;

  const features: FeatureFlags = useSelector(
    ({ sys }: ApplicationState) => sys.features
  );

  useEffect(() => {
    dispatch(requestAdminCase(case_id));
  }, [case_id]);

  const onSavePatient = (newPatient: Patient) => {
    dispatch(savePatient({ patient: newPatient, adminCase_id: adminCase?.id }));
  };

  const onSaveCase = (newCase: Partial<CaseAdmin>) => {
    dispatch(saveAdminCase(newCase));
  };

  const onSaveIntegration = (
    payload: Parameters<typeof updateIntegrations>[0]
  ) => {
    dispatch(updateIntegrations(payload));
  };

  const onSaveQAReview = (payload: any) => {
    dispatch(createQAReview(payload));
  };

  const onResetErrorMessage = () => {
    dispatch(resetCaseErrorMessage());
  };

  const onResetDeletionStatus = () => {
    dispatch(resetDeletionStatus());
  };

  return adminCase ? (
    <EditCasePanel
      adminCase={adminCase}
      isDeleting={isDeleting}
      setIsDeleting={setIsDeleting}
      resetErrorMessage={onResetErrorMessage}
      resetDeletionStatus={onResetDeletionStatus}
      onSaveCase={onSaveCase}
      onSavePatient={onSavePatient}
      onSaveQAReview={onSaveQAReview}
      onSaveIntegration={onSaveIntegration}
      slideId={slide_id}
      documentId={document_id}
      documentUploading={documentQuery === "upload"}
      specimenId={specimen_id}
      blockId={block_id}
      orderId={order_id}
      features={features}
      historyState={historyState}
    />
  ) : (
    <Spinner />
  );
};

export default CaseEditController;
